import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ProcurementAdminPermissions } from '../../utils/permissions';
import OpenRequests from '../../components/ProcurementRequestPO/OpenRequests';
import PendingApproval from '../../components/ProcurementRequestPO/PendingApproval';
import AwardedRequests from '../../components/ProcurementRequestPO/AwardedRequests';
import ArchivedRequests from '../../components/ProcurementRequestPO/ArchivedRequests';
import useProcurementRequests from '../../hooks/useProcurementRequests';
import { Tabs, Tab, Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ProcurementPoTutorialVideo } from '../../config/tutorials';

const ProcurementRequestPO = () => {
  const { fetchProcurementRequests, loading, error } = useProcurementRequests('PO Request');
  const [openRequests, setOpenRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [awardedRequests, setAwardedRequests] = useState([]);
  const [archivedRequests, setArchivedRequests] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const { userPermissions, permsLoading } = useAuth();

  if (permsLoading) {
    return (
      <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
        <Typography variant="h6">Loading User Permissions</Typography>
      </Box>
    );
  }

  const isProcurementAdmin = useMemo(
    () => userPermissions.some((perm) => ProcurementAdminPermissions.includes(perm)),
    [userPermissions]
  );

  const refetchData = useCallback(async () => {
    const result = await fetchProcurementRequests();
    setOpenRequests(result.open);
    setPendingRequests(result.awaitingApproval);
    setAwardedRequests(result.closed);
    setArchivedRequests(result.archived);
  }, [fetchProcurementRequests]);

  useEffect(() => {
    refetchData();
  }, [refetchData]);

  const tabConfig = [
    {
      label: `Open Requests (${openRequests.length})`,
      component: <OpenRequests data={openRequests} refetch={refetchData} />,
    },
    {
      label: `Pending Approval (${pendingRequests.length})`,
      component: <PendingApproval data={pendingRequests} refetch={refetchData} />,
    },
    {
      label: `Awarded Requests`,
      component: <AwardedRequests data={awardedRequests} refetch={refetchData} />,
    },
  ];

  if (isProcurementAdmin) {
    tabConfig.push({
      label: `Archived Requests`,
      component: <ArchivedRequests data={archivedRequests} refetch={refetchData} />,
    });
  }

  const handleTabChange = (e, newValue) => setActiveTab(newValue);

  return (
    <Box sx={{ width: '100%', padding: '20px' }}>
      {/* Tabs + Tutorial Icon */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTab-root': {
              minWidth: '180px',
              fontSize: '16px',
              padding: '12px 20px',
              textTransform: 'none',
            },
            '& .MuiTabs-indicator': {
              height: '4px',
              borderRadius: '2px',
            },
          }}
        >
          {tabConfig.map((tab, i) => (
            <Tab key={i} label={tab.label} />
          ))}
        </Tabs>

        <Tooltip title="Watch Tutorial">
          <IconButton
            component="a"
            href={ProcurementPoTutorialVideo}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
          >
            <HelpOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Loading / Tab Content */}
      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <CircularProgress />
          <p>Loading data...</p>
        </Box>
      ) : (
        tabConfig[activeTab]?.component || null
      )}
    </Box>
  );
};

export default ProcurementRequestPO;
